import { useState, useEffect } from 'react';
import { reCaptcha } from '../../utils/reCaptcha';
import dayjs from "dayjs";
import {
  Button,
  Typography,
  Container,
  Grid,
  Box
} from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
import { updateUserRequest, deleteUserRequest } from '../../remote/usersRequests';
import theme from '../../components/LayoutComponents/theme';
import PurpleButton from '../../components/LayoutComponents/PurpleButton';
import UserForm from './UserForm';
import { passwordRegex } from '../../staticDataSet';
import LoadingCircle from '../../components/LayoutComponents/LoadingCircle';
import { getUserRequest } from '../../remote/usersRequests';

function UpdateUserPage({ session, profile, setProfile }) {
  const [errors, setErrors] = useState({});
  const [captchaValue, setCaptchaValue] = useState(null);
  const [pictureName, setPictureName] = useState('');
  const [loading, setLoading] = useState(false);

  const initialFormData = {
    username: profile.username || '',
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    password: '',
    confirmPassword: '',
    addressLine1: profile.addressLine1 || '',
    addressLine2: profile.addressLine2 || '',
    city: profile.city || '',
    state: profile.state || '',
    zipCode: profile.zipCode || '',
    country: 'United States',
    primaryPhone: profile.primaryPhone || '',
    alternatePhone: profile.alternatePhone || '',
    eveningPhone: profile.eveningPhone || '',
    about: profile.about || '',
    militaryService: profile.militaryService || false,
    militaryServiceBranch: profile.militaryServiceBranch || '',
    militaryServiceStartDate: profile.militaryServiceStartDate ? dayjs(profile.militaryServiceStartDate) : null,
    militaryServiceEndDate: profile.militaryServiceEndDate ? dayjs(profile.militaryServiceEndDate) : null,
    militaryServiceStatus: profile.militaryServiceStatus || '',
    militaryCategoryId: profile.militaryCategoryId || null,
    profilePic: null,
    resume: null,
    isAnonymous: profile.anonymous || false
  };
  const [formData, setFormData] = useState(initialFormData);

  const isPasswordValid = (password) => passwordRegex.test(password);

  const validateForm = () => {
    const { username, password, confirmPassword, militaryService, militaryServiceBranch, militaryServiceEndDate, militaryServiceStartDate, militaryServiceStatus } = formData;

    let newErrors = {};
    if (!username) newErrors.username = 'Username is required';
    if (password && !isPasswordValid(password)) newErrors.password = 'Password must be 8-16 characters long, include at least one uppercase letter, and one special character.';
    if (password && password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match.';
    if (password.length > 16) newErrors.password = 'Password cannot exceed 16 characters.';
    if (!captchaValue) newErrors.captcha = 'reCAPTCHA token not generated.';
    if (militaryService) {
      if (!militaryServiceBranch || militaryServiceBranch === "") newErrors.militaryServiceBranch = 'Military Service Branch is required';
      if (!militaryServiceStartDate) newErrors.militaryServiceStartDate = 'Military Service Start Date is required';
      if (!militaryServiceEndDate) newErrors.militaryServiceEndDate = 'Military Service End Date is required';
      if (!militaryServiceStatus || militaryServiceStatus === "") newErrors.militaryServiceStatus = 'Military Status is required';
      if (militaryServiceStartDate && militaryServiceEndDate) {
        const start = new Date(militaryServiceStartDate);
        const end = new Date(militaryServiceEndDate);
        if (start > end) newErrors.militaryServiceEndDate = 'End Date must be after Start Date';
      }
    }

    return newErrors;
  };





  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTimeout(() => setErrors({}), 5000);
      return;
    }

    try {
      setLoading(true);
      await updateUserRequest(session?.token, {
        id: session.userId,
        username: formData.username,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        primaryPhone: formData.primaryPhone,
        alternatePhone: formData.alternatePhone,
        militaryService: formData.militaryService,
        militaryServiceBranch: formData.militaryServiceBranch,
        militaryServiceStartDate: formData.militaryServiceStartDate,
        militaryServiceEndDate: formData.militaryServiceEndDate,
        militaryServiceStatus: formData.militaryServiceStatus,
        militaryCategoryId: formData.militaryCategoryId,
        logoFileName: pictureName,
        isAnonymous: formData.isAnonymous
      });
      let res = await getUserRequest(session?.token);
      setProfile(res.data || {});
      alert('User updated successfully.');
      
    } catch (err) {
      alert('Failed to update user. Please try again.');
    } finally {
      setLoading(false);
      refreshCaptcha();
    };
  }

  const refreshCaptcha = () => {
    reCaptcha("updateUser", (token) => setCaptchaValue(token));
  };

  useEffect(() => {
    if (!captchaValue) refreshCaptcha();
  }, [captchaValue]);

  return (
    <div>
      {/* Loading Spinner */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            flexDirection: 'column',
            gap: 2
          }}
        >
          <LoadingCircle />
        </Box>
      )}

      <Container component="main" maxWidth="md" sx={{ height: 'auto', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ padding: '1rem', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom color={theme.colors.purple}>Profile</Typography>
          </Box>

          {/* Display errors & form */}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={2} justifyContent="center">
                <UserForm
                  session={session}
                  formData={formData}
                  setFormData={setFormData}
                  formType="edit"
                  error={errors}
                />

                {/* Resume Upload */}
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '1rem', gap: 1 }}>
                  <PurpleButton type="submit" label="Update User" />
                </Box>
              </Grid>

              {/* Delete Account */}
              <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  sx={{ fontWeight: theme.typography.fontWeightBold }}
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                      try {
                        await deleteUserRequest(session.token, session.userId);
                        alert('Account deleted successfully.');
                        window.location.href = 'https://app.nationaltalenthub.com';
                      } catch (err) {
                        alert('Failed to delete account. Please try again.');
                      }
                    }
                  }}
                >
                  Delete Account
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </div>
  );
}

export default UpdateUserPage;
